// ============================================================================
// NutritionTargetsCard — the client's DAILY TARGETS at a glance (Wave 2).
// ----------------------------------------------------------------------------
// A clean, readable, mobile-first card (the fancy animated rings are Wave 4):
// each of kcal / protein / carbs / fat / water shows today's LOGGED intake vs
// the target as a labelled bar. Targets come from GET /me/nutrition-targets
// (general guidance, auto-derived from the client's stats + goal); intake comes
// from the day's meal totals + water total. Always surfaces the non-medical
// disclaimer. When the profile lacks the inputs BMR/TDEE need, the macro targets
// are null and we show a "fill in your stats" hint instead of empty bars.
// Pulse, EN + 繁中, reduced-motion friendly (CSS-only bar fills).
// ============================================================================
import { useTranslation } from "react-i18next";
import { Banner, Card, CardHeader } from "../../components";
import { useClientMeals, useClientWater, useNutritionTargets, todayDay } from "../hooks";
import { fmtNum } from "./portalLib";

type Accent = "coral" | "violet" | "ocean" | "lime" | "amber";

const BAR: Record<Accent, string> = {
  coral: "bg-grad-coral",
  violet: "bg-grad-violet",
  ocean: "bg-grad-ocean",
  lime: "bg-grad-lime",
  amber: "bg-grad-amber",
};

function ProgressRow({
  label,
  logged,
  target,
  unit,
  accent,
}: {
  label: string;
  logged: number;
  target: number | null;
  unit: string;
  accent: Accent;
}) {
  const { t } = useTranslation("nutrition");
  const pct =
    target && target > 0 ? Math.min(100, Math.round((logged / target) * 100)) : 0;
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="font-display text-sm font-bold text-body">{label}</span>
        <span className="text-sm tabular-nums text-body-soft">
          {target != null
            ? t("target.loggedOfTarget", {
                logged: fmtNum(logged, 0),
                target: fmtNum(target, 0),
                unit,
              })
            : `${fmtNum(logged, 0)} ${unit}`}
        </span>
      </div>
      <div
        className="h-2.5 w-full overflow-hidden rounded-pill bg-surface-sunken"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className={`h-full rounded-pill ${BAR[accent]} transition-[width] duration-500 ease-kinetic`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function NutritionTargetsCard() {
  const { t } = useTranslation("nutrition");
  const targets = useNutritionTargets();
  const meals = useClientMeals(todayDay());
  const water = useClientWater();

  if (targets.isLoading) {
    return (
      <Card>
        <CardHeader title={t("target.title")} />
        <div className="space-y-4" aria-hidden>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-7 animate-pulse rounded-md bg-surface-sunken" />
          ))}
        </div>
      </Card>
    );
  }
  if (targets.isError || !targets.data) {
    return (
      <Card>
        <CardHeader title={t("target.title")} />
        <Banner tone="warning" title={t("target.error")} />
      </Card>
    );
  }

  const d = targets.data;
  const totals = meals.data?.totals;
  const loggedKcal = totals?.kcal ?? 0;
  const loggedProtein = totals?.protein_g ?? 0;
  const loggedCarbs = totals?.carbs_g ?? 0;
  const loggedFat = totals?.fat_g ?? 0;

  // Today's water total (sum the row for today, if any).
  const today = todayDay();
  const loggedWater =
    water.data?.find((w) => w.day === today)?.ml ?? 0;

  const sourceLabel = d.source === "coach" ? t("target.coach") : t("target.auto");
  const macrosMissing = d.kcal == null && d.protein_g == null;

  return (
    <Card className="space-y-4">
      <CardHeader
        title={t("target.title")}
        subtitle={`${t("target.source")}: ${sourceLabel}`}
      />

      {macrosMissing ? (
        <Banner tone="info" title={t("target.needMore")} />
      ) : (
        <div className="space-y-4">
          <ProgressRow
            label={t("macro.kcal")}
            logged={loggedKcal}
            target={d.kcal}
            unit={t("macro.kcalUnit")}
            accent="coral"
          />
          <ProgressRow
            label={t("macro.protein")}
            logged={loggedProtein}
            target={d.protein_g}
            unit={t("macro.gUnit")}
            accent="violet"
          />
          <ProgressRow
            label={t("macro.carbs")}
            logged={loggedCarbs}
            target={d.carbs_g}
            unit={t("macro.gUnit")}
            accent="amber"
          />
          <ProgressRow
            label={t("macro.fat")}
            logged={loggedFat}
            target={d.fat_g}
            unit={t("macro.gUnit")}
            accent="lime"
          />
        </div>
      )}

      <ProgressRow
        label={t("water.title")}
        logged={loggedWater}
        target={d.water_ml}
        unit={t("water.mlUnit")}
        accent="ocean"
      />

      <p className="rounded-md bg-surface-sunken px-3 py-2.5 text-xs leading-relaxed text-body-muted">
        {t("disclaimer")}
      </p>
    </Card>
  );
}
